import { CheckIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "src/components/Shared/Buttons/Button";
import { classNames } from "src/helpers/classNames";

const FLASH_MS = 2500;

// Remembers which action just succeeded for a moment, so its button can confirm in place instead of a toast.
export const useFlash = (durationMs = FLASH_MS) => {
  const [flashKey, setFlashKey] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback(
    (key) => {
      clearTimeout(timer.current);
      setFlashKey(key);
      timer.current = setTimeout(() => setFlashKey(null), durationMs);
    },
    [durationMs],
  );
  return [flashKey, flash];
};

// A Button that turns green with a popping check mark and `doneLabel` while `done` is true.
// While confirming it ignores `disabled` (the page may already have disabled it, e.g. a deployment just started)
// but cannot be clicked.
const FeedbackButton = ({ done = false, doneLabel = "Done", children, className = "", disabled = false, onClick, ...props }) => (
  <Button
    {...props}
    disabled={done ? false : disabled}
    onClick={done ? undefined : onClick}
    className={classNames(className, "transition-colors duration-200", done && "pointer-events-none !border-green-300 !bg-green-50 !text-green-700")}>
    <AnimatePresence
      initial={false}
      exitBeforeEnter>
      {done ? (
        <motion.span
          key="done"
          className="flex items-center gap-x-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}>
          <motion.span
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: [0, 1.35, 1], rotate: 0 }}
            transition={{ duration: 0.35 }}>
            <CheckIcon className="h-4 w-4 stroke-[2.5]" />
          </motion.span>
          <span aria-live="polite">{doneLabel}</span>
        </motion.span>
      ) : (
        <motion.span
          key="idle"
          className="flex items-center gap-x-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}>
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  </Button>
);

export default FeedbackButton;
